import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiBarChart2, FiCheckCircle, FiX } from 'react-icons/fi';
import { FaTrophy } from 'react-icons/fa';
import { useApi } from '../../hooks/useApi';
import { getProfileImageUrl } from '../../utils/imageUtils';
import { formatSignedUsd } from '../../utils/formatUsd';
import ProfitLoss from './ProfitLoss';

const MASTER_LEADERBOARD_TABS = [
  { id: 'forex-gold', label: 'Forex Gold', brokerType: 'genesis' },
  { id: 'webull', label: 'Webull', brokerType: 'webull' },
];

const HEX_CLIP = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

const RANK_THEMES = {
  1: {
    label: '1st PLACE',
    border: 'border-amber-400/70',
    glow: 'shadow-[0_0_40px_rgba(251,191,36,0.35),inset_0_0_30px_rgba(251,191,36,0.06)]',
    podium: 'from-amber-500/40 via-amber-600/20 to-transparent',
    podiumRing: 'border-amber-400/50 shadow-[0_0_30px_rgba(251,191,36,0.4)]',
    badge: 'from-amber-300 via-amber-500 to-amber-700',
    badgeText: 'text-amber-950',
    placeText: 'text-amber-600 dark:text-amber-400',
    img: '/badge1st.png',
    btn: 'from-sky-500 via-blue-600 to-indigo-800 hover:from-sky-400 hover:via-blue-500 hover:to-indigo-700',
  },
  2: {
    label: '2nd PLACE',
    border: 'border-violet-400/70',
    glow: 'shadow-[0_0_40px_rgba(167,139,250,0.35),inset_0_0_30px_rgba(167,139,250,0.06)]',
    podium: 'from-violet-500/40 via-purple-600/20 to-transparent',
    podiumRing: 'border-violet-400/50 shadow-[0_0_30px_rgba(167,139,250,0.4)]',
    badge: 'from-violet-300 via-purple-500 to-violet-800',
    badgeText: 'text-violet-950',
    placeText: 'text-violet-600 dark:text-violet-400',
    img: '/badge2nd.png',
    btn: 'from-violet-500 via-purple-600 to-indigo-900 hover:from-violet-400 hover:via-purple-500 hover:to-indigo-800',
  },
  3: {
    label: '3rd PLACE',
    border: 'border-cyan-400/60',
    glow: 'shadow-[0_0_35px_rgba(34,211,238,0.28),inset_0_0_25px_rgba(34,211,238,0.05)]',
    podium: 'from-cyan-500/35 via-sky-600/15 to-transparent',
    podiumRing: 'border-cyan-400/45 shadow-[0_0_25px_rgba(34,211,238,0.35)]',
    badge: 'from-cyan-300 via-sky-500 to-cyan-800',
    badgeText: 'text-cyan-950',
    placeText: 'text-cyan-600 dark:text-cyan-400',
    img: '/badge3rd.png',
    btn: 'from-cyan-500 via-sky-600 to-blue-900 hover:from-cyan-400 hover:via-sky-500 hover:to-blue-800',
  },
};

const formatLeaderboardPnl = (value) => {
  const n = Number(value) || 0;
  if (!Number.isFinite(n)) return '-';
  if (n === 0) return '$0';
  return formatSignedUsd(n);
};

const getInitials = (name) => {
  if (!name) return 'TR';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const AvatarContent = ({ trader }) => {
  const [broken, setBroken] = useState(false);
  const raw = String(trader?.avatar || '').trim();
  React.useEffect(() => setBroken(false), [raw]);
  if (!raw || broken) {
    return <span className="text-lg font-bold text-foreground">{getInitials(trader?.name)}</span>;
  }
  return (
    <img
      src={getProfileImageUrl(raw)}
      alt={trader?.name || 'Trader'}
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
};

const LeaderboardMarketTabs = ({ activeTab, onChange }) => (
  <div className="flex justify-center">
    <div
      className="inline-flex w-full max-w-[300px] rounded-full border border-border bg-surface-elevated/90 p-1 shadow-sm backdrop-blur-sm"
      role="tablist"
      aria-label="Leaderboard market"
    >
      {MASTER_LEADERBOARD_TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
              isActive
                ? 'bg-gradient-to-r from-violet-600/80 to-blue-600/80 text-white shadow-lg shadow-violet-500/25'
                : 'text-muted hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  </div>
);

const RankBadge = ({ theme, rank }) => {
  const [imgBroken, setImgBroken] = useState(false);

  if (theme.img && !imgBroken) {
    return (
      <img
        src={theme.img}
        alt={theme.label}
        className="h-17 w-10 shrink-0 object-contain object-left drop-shadow-[0_0_8px_rgba(251,191,36,0.35)]"
        onError={() => setImgBroken(true)}
      />
    );
  }

  return (
    <div
      className={`flex h-11 w-10 shrink-0 items-center justify-center bg-gradient-to-b ${theme.badge} shadow-lg`}
      style={{ clipPath: HEX_CLIP }}
    >
      <span className={`text-xs font-black ${theme.badgeText}`}>{rank}</span>
    </div>
  );
};

const PodiumCard = ({ trader, rank, onViewStats, platformLabel }) => {
  const theme = RANK_THEMES[rank] || RANK_THEMES[3];
  const pnl = Number(trader.pnl || 0);
  const roi = Number(trader.roi || 0);
  const winRate = Number(trader.winRate || 0);
  const pnlDisplay = formatLeaderboardPnl(pnl);
  const roiDisplay = `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`;
  const pnlColor = pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const roiColor = roi >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-orange-600 dark:text-orange-400';

  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative w-full max-w-[420px] overflow-hidden rounded-2xl border bg-surface-elevated/95 backdrop-blur-md ${theme.border} ${theme.glow}`}
      >
        <div className="relative px-5 pb-5 pt-4">
          <div className="mb-4 flex items-start justify-between">
            <RankBadge theme={theme} rank={rank} />
            <span className={`pt-1 text-xs font-bold tracking-widest ${theme.placeText}`}>
              {theme.label}
            </span>
          </div>

          <div className="flex flex-col items-center text-center">
            <div
              className="mb-4 flex h-[88px] w-[80px] items-center justify-center overflow-hidden border-2 border-border bg-surface shadow-xl"
              style={{ clipPath: HEX_CLIP }}
            >
              <AvatarContent trader={trader} />
            </div>

            <div className="flex items-center gap-1.5">
              <h3 className="text-xl font-bold text-foreground">{trader.name}</h3>
              <FiCheckCircle className="h-4 w-4 shrink-0 text-sky-400" aria-label="Verified" />
            </div>
            <p className="mt-1 text-sm text-muted">{platformLabel}</p>

            <div className="mt-5">
              <p className={`text-4xl font-bold tabular-nums tracking-tight ${pnlColor}`}>{pnlDisplay}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-widest text-muted">P&amp;L</p>
            </div>

            <div className="mt-5 grid w-full grid-cols-3 gap-2">
              <div className="rounded-xl border border-border bg-surface px-2 py-3 text-center">
                <p className={`truncate text-sm font-bold tabular-nums ${pnlColor}`}>{pnlDisplay}</p>
                <p className="mt-1 text-[10px] text-muted">P&amp;L</p>
              </div>
              <div className="rounded-xl border border-border bg-surface px-2 py-3 text-center">
                <p className="truncate text-sm font-bold tabular-nums text-amber-600 dark:text-amber-400">{winRate.toFixed(1)}%</p>
                <p className="mt-1 text-[10px] text-muted">Win Rate</p>
              </div>
              <div className="rounded-xl border border-border bg-surface px-2 py-3 text-center">
                <p className={`truncate text-sm font-bold tabular-nums ${roiColor}`}>{roiDisplay}</p>
                <p className="mt-1 text-[10px] text-muted">ROI</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onViewStats(trader)}
              disabled={!trader.masterId}
              className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 ${theme.btn}`}
            >
              <FiBarChart2 className="h-4 w-4" />
              View Stats
            </button>
          </div>
        </div>
      </div>

      <div className="relative -mt-2 flex w-full max-w-[320px] flex-col items-center">
        <div
          className={`h-3 w-full rounded-[50%] border bg-gradient-to-b ${theme.podium} ${theme.podiumRing}`}
        />
        <div className={`h-2 w-[85%] rounded-[50%] border bg-gradient-to-b ${theme.podium} opacity-60`} />
      </div>
    </div>
  );
};

const CompactRow = ({ trader, onViewStats, platformLabel }) => {
  const pnl = Number(trader.pnl || 0);
  const roi = Number(trader.roi || 0);
  const winRate = Number(trader.winRate || 0);
  const pnlDisplay = formatLeaderboardPnl(pnl);
  const roiDisplay = `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`;
  const pnlColor = pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface-elevated/90 px-4 py-4 shadow-sm backdrop-blur-sm md:flex-nowrap">
      <span className="w-8 text-2xl font-black italic tabular-nums text-foreground/70">{trader.rank}</span>

      <div
        className="flex h-11 w-10 shrink-0 items-center justify-center overflow-hidden border border-border bg-surface"
        style={{ clipPath: HEX_CLIP }}
      >
        <AvatarContent trader={trader} />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate font-bold text-foreground">{trader.name}</p>
        <p className="text-xs text-muted">
          {platformLabel} · {trader.trades} trades · {Number(trader.followers || 0)} subs
        </p>
      </div>

      <div className="hidden gap-6 sm:flex">
        <div className="text-center">
          <p className="text-[10px] uppercase text-muted">Win</p>
          <p className="text-sm font-bold text-amber-600 dark:text-amber-400">{winRate.toFixed(1)}%</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-muted">ROI</p>
          <p className="text-sm font-bold text-orange-600 dark:text-orange-400">{roiDisplay}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-muted">P&amp;L</p>
          <p className={`text-sm font-bold ${pnlColor}`}>{pnlDisplay}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onViewStats(trader)}
        disabled={!trader.masterId}
        className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-500/20 disabled:opacity-50 dark:bg-violet-600/20 dark:text-violet-200 dark:hover:bg-violet-600/35"
      >
        <FiBarChart2 className="h-3.5 w-3.5" />
        View Stats
      </button>
    </div>
  );
};

const StatsModal = ({ statsTrader, onClose }) => {
  if (!statsTrader || typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[220] overflow-y-auto bg-black/80 p-2 backdrop-blur-md sm:p-4">
      <div className="mx-auto w-full max-w-[1200px] pt-6 sm:pt-8">
        <div className="relative w-full rounded-3xl border border-border bg-gradient-to-b from-surface-elevated to-surface p-1 shadow-[0_24px_80px_rgba(0,0,0,0.35)] md:p-3">
          <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4">
            <div className="min-w-0 flex-1 px-2 text-center sm:px-6">
              <h3 className="text-base font-bold text-foreground sm:text-lg md:text-xl">
                {statsTrader.name} Stats
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-surface-elevated text-foreground transition-all hover:bg-surface"
              aria-label="Close stats modal"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
          <ProfitLoss key={statsTrader.masterId} selectedBrokerId={null} masterUserId={statsTrader.masterId} />
        </div>
      </div>
    </div>,
    document.body
  );
};

const TopTraderLeaderboard = () => {
  const [activeTab, setActiveTab] = useState('webull');
  const [statsTrader, setStatsTrader] = useState(null);

  const activeConfig = MASTER_LEADERBOARD_TABS.find((t) => t.id === activeTab) || MASTER_LEADERBOARD_TABS[0];
  const isForexGoldTab = activeTab === 'forex-gold';
  const platformLabel = isForexGoldTab ? 'Forex Gold' : 'Webull';

  const leaderboardQuery = useApi.useTopTraderLeaderboard(activeConfig.brokerType);

  const rankedTraders = useMemo(() => {
    const apiRows = leaderboardQuery.data?.rows || [];
    return apiRows.map((row) => ({
      rank: row.rank,
      name: row.name,
      avatar: row.avatar || '',
      market: row.market || platformLabel,
      pnl: row.pnl,
      roi: row.roi,
      winRate: row.winRate,
      trades: row.trades,
      followers: row.followers,
      status: row.status,
      masterId: row.masterId,
    }));
  }, [leaderboardQuery.data?.rows, platformLabel]);

  const topPodium = rankedTraders.slice(0, 2);
  const thirdPlace = rankedTraders[2];
  const rowsBelow = rankedTraders.slice(3);

  const isLoading = leaderboardQuery.isLoading;
  const isError = leaderboardQuery.isError;

  const titlePrefix = isForexGoldTab ? 'Forex Gold' : 'Webull';
  const titleSuffix = 'Master Performance';

  return (
    <div className="animate-fade-in w-full min-w-0">
      <div className="relative min-h-[600px] w-full overflow-hidden rounded-2xl border border-border bg-surface px-4 py-10 md:px-8">
        {/* Background glow streaks */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-20 top-1/4 h-[500px] w-[500px] rounded-full bg-blue-500/10 blur-[120px] dark:bg-blue-600/20" />
          <div className="absolute -right-20 top-1/3 h-[500px] w-[500px] rounded-full bg-violet-500/10 blur-[120px] dark:bg-violet-600/25" />
          <div className="absolute bottom-0 left-1/2 h-64 w-[80%] -translate-x-1/2 rounded-full bg-indigo-500/5 blur-[80px] dark:bg-indigo-600/10" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl">
          <div className="mb-8">
            <LeaderboardMarketTabs
              activeTab={activeTab}
              onChange={(tabId) => {
                setActiveTab(tabId);
                setStatsTrader(null);
              }}
            />
          </div>

          <div className="mb-10 text-center">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 scale-150 rounded-full bg-amber-400/20 blur-xl" />
                <FaTrophy className="relative h-10 w-10 text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.8)]" />
              </div>
            </div>

            <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
              <span className="bg-gradient-to-r from-violet-300 via-blue-300 to-violet-400 bg-clip-text text-transparent">
                {titlePrefix}
              </span>{' '}
              <span className="text-foreground">Trader</span>{' '}
              <span className="bg-gradient-to-r from-violet-300 via-blue-300 to-violet-400 bg-clip-text text-transparent">
                {titleSuffix}
              </span>
            </h1>
            <p className="mt-3 text-sm text-muted md:text-base">
              Top Traders. Real Performance. Verified Results.
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl border border-border bg-surface-elevated px-4 py-3 text-center text-sm text-muted">
              Loading leaderboard…
            </div>
          )}
          {isError && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center text-sm text-red-300">
              Failed to load leaderboard. Please try again.
            </div>
          )}
          {!isLoading && !isError && rankedTraders.length === 0 && (
            <div className="mb-6 rounded-xl border border-border bg-surface-elevated px-4 py-10 text-center text-sm text-muted">
              No {isForexGoldTab ? 'Genesis' : 'Webull'} masters found yet.
            </div>
          )}

          {topPodium.length > 0 && (
            <div className="mb-12 grid grid-cols-1 items-end gap-10 lg:grid-cols-2 lg:gap-8">
              {topPodium.map((trader) => (
                <PodiumCard
                  key={trader.masterId || trader.rank}
                  trader={trader}
                  rank={trader.rank}
                  platformLabel={trader.market || platformLabel}
                  onViewStats={setStatsTrader}
                />
              ))}
            </div>
          )}

          {thirdPlace && (
            <div className="mb-10 flex justify-center">
              <div className="w-full max-w-[420px]">
                <PodiumCard
                  trader={thirdPlace}
                  rank={3}
                  platformLabel={thirdPlace.market || platformLabel}
                  onViewStats={setStatsTrader}
                />
              </div>
            </div>
          )}

          {rowsBelow.length > 0 && (
            <div className="mx-auto max-w-4xl space-y-3">
              {rowsBelow.map((trader) => (
                <CompactRow
                  key={trader.masterId || trader.rank}
                  trader={trader}
                  platformLabel={trader.market || platformLabel}
                  onViewStats={setStatsTrader}
                />
              ))}
            </div>
          )}

          <StatsModal statsTrader={statsTrader} onClose={() => setStatsTrader(null)} />
        </div>
      </div>
    </div>
  );
};

export default TopTraderLeaderboard;
